import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import {
  getConfigDoctorPatientApi,
  getConfigurationApi,
  getInAppNotificationApi,
} from "@/utils/apis/config";

import { IConfigDetail } from "@/models/ApiRequestResponse/doctor";
import { IFilterType } from "@/models/patient";
import {
  INotificationDaum,
  INotificationStatus,
  IParams,
} from "@/models/ApiRequestResponse/notification";
import { IConfigKey, ICountryCodeList, IRedirectAfterLogin } from "@/models";

import { RootState } from "../store";

export const fetchConfiguration = createAsyncThunk(
  "doctor-patient/configuration",
  async (filterKey?: IFilterType) => {
    const response = await getConfigDoctorPatientApi(filterKey);
    return response;
  }
);

export const fetchInAppNotification = createAsyncThunk(
  "doctor-patient/inAppNotification",
  async (params: IParams) => {
    const response = await getInAppNotificationApi(params);
    return response;
  }
);

export const fetchCountryCode = createAsyncThunk(
  "doctor-patient/countryCode",
  async () => {
    const response = await getConfigurationApi(IConfigKey.COUNTRY_CODE);
    return response;
  }
);
const configurationSlice = createSlice({
  name: "config",
  initialState: {
    config: {} as IConfigDetail,
    isAppNotificationLoading: false as boolean,
    isAllNotificationRead: false as boolean,
    inAppNotification: [] as INotificationDaum[],
    isShowUniversalSearch: false as boolean,
    isSettingPreferencesOpenClose: false as boolean,
    isRequestCallBackOpen: false as boolean,
    isInfiniteScrollFilterChanges: false as boolean,
    isShowSuccessModal: false as boolean,
    location: "" as string,
    isLoading: true as boolean,
    isShowNotification: false as boolean,
    unreadNotificationIds: [] as string[],
    countryCodeList: {} as ICountryCodeList,
    isShowChangeLocationModal: false as boolean,
    redirectAfterLogin: {} as IRedirectAfterLogin,
    isShowEditProfileModal: false as boolean,
    isLocationAccessOpen: false as boolean,
  },
  reducers: {
    setIsShowUniversalSearch: (state, action) => {
      state.isShowUniversalSearch = action.payload;
    },
    setIsSettingPreferencesOpenClose: (state) => {
      state.isSettingPreferencesOpenClose =
        !state.isSettingPreferencesOpenClose;
    },
    setIsRequestCallBackOpen: (state) => {
      state.isRequestCallBackOpen = !state.isRequestCallBackOpen;
    },
    handleIsInfiniteScrollFilterChanges: (state) => {
      state.isInfiniteScrollFilterChanges =
        !state.isInfiniteScrollFilterChanges;
    },
    handleIsShowSuccessModal: (state) => {
      state.isShowSuccessModal = !state.isShowSuccessModal;
    },
    setLocation: (state, action) => {
      state.location = action.payload;
    },
    setIsShowNotification: (state, action) => {
      state.isShowNotification = action.payload;
    },
    resetUnreadInAppNotification: (state) => {
      state.unreadNotificationIds = [];
    },
    setInAppNotification: (state, action) => {
      state.inAppNotification = action.payload;
    },
    setIsShowChangeLocationModal: (state, action) => {
      state.isShowChangeLocationModal = action.payload;
    },
    setRedirectAfterLogin: (state, action) => {
      state.redirectAfterLogin = action.payload;
    },
    setIsShowEditProfileModal: (state, action) => {
      state.isShowEditProfileModal = action.payload;
    },
    setIsLocationAccessOpen: (state, action) => {
      state.isLocationAccessOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConfiguration.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchConfiguration.fulfilled, (state, action) => {
        state.isLoading = false;
        if ("result" in action.payload) {
          state.config = { ...state.config, ...action.payload.result };
        }
      })
      .addCase(fetchConfiguration.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchInAppNotification.pending, (state) => {
        state.isAppNotificationLoading = true;
      })
      .addCase(fetchInAppNotification.fulfilled, (state, action) => {
        state.isAppNotificationLoading = false;
        if ("result" in action.payload) {
          state.inAppNotification = [
            ...state.inAppNotification,
            ...action.payload.result.notificationData,
          ];
          state.isAllNotificationRead =
            action.payload.result.isAllNotificationRead;
          state.unreadNotificationIds = [
            ...new Set([
              ...state.unreadNotificationIds,
              ...action.payload.result.notificationData
                ?.filter(
                  (notification) =>
                    notification.status === INotificationStatus.UNREAD
                )
                ?.map((notification) => notification.id),
            ]),
          ];
        }
      })
      .addCase(fetchInAppNotification.rejected, (state) => {
        state.isAppNotificationLoading = false;
      })
      .addCase(fetchCountryCode.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCountryCode.fulfilled, (state, action) => {
        state.isLoading = false;
        if ("result" in action.payload) {
          state.countryCodeList = action.payload.result;
        }
      })
      .addCase(fetchCountryCode.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

const selectConfig = (state: RootState) => state.config;

export const config = createSelector([selectConfig], (selectConfig) => {
  return {
    configDetail: selectConfig.config,
    location: selectConfig.location,
    isShowUniversalSearch: selectConfig.isShowUniversalSearch,
    isShowNotification: selectConfig.isShowNotification,
    isSettingPreferencesOpenClose: selectConfig.isSettingPreferencesOpenClose,
    inAppNotification: selectConfig.inAppNotification,
    isAppNotificationLoading: selectConfig.isAppNotificationLoading,
    isAllNotificationRead: selectConfig.isAllNotificationRead,
    unreadNotificationIds: selectConfig.unreadNotificationIds,
    countryCodeList: selectConfig.countryCodeList,
    isShowChangeLocationModal: selectConfig.isShowChangeLocationModal,
    redirectAfterLogin: selectConfig.redirectAfterLogin,
    isRequestCallBackOpen: selectConfig.isRequestCallBackOpen,
    isInfiniteScrollFilterChanges: selectConfig.isInfiniteScrollFilterChanges,
    isShowSuccessModal: selectConfig.isShowSuccessModal,
    isShowEditProfileModal: selectConfig.isShowEditProfileModal,
    isLocationAccessOpen: selectConfig.isLocationAccessOpen,
  };
});

export const {
  setIsShowUniversalSearch,
  setIsSettingPreferencesOpenClose,
  setIsRequestCallBackOpen,
  handleIsInfiniteScrollFilterChanges,
  handleIsShowSuccessModal,
  setLocation,
  setIsShowNotification,
  resetUnreadInAppNotification,
  setInAppNotification,
  setIsShowChangeLocationModal,
  setRedirectAfterLogin,
  setIsShowEditProfileModal,
  setIsLocationAccessOpen,
} = configurationSlice.actions;
export default configurationSlice.reducer;
